import { cva, type VariantProps } from 'class-variance-authority';
import type { ComponentPropsWithoutRef, ElementType, ReactNode } from 'react';
import { cn } from '@/lib/utils';

/**
 * The Tailwind face of MUI's `Typography` — a faithful port, not a redesign.
 *
 * Unlike `Box`/`Grid`, Typography is NOT a shallow pass-through: it carries a variant
 * scale, a mode-dependent default colour, a tabIndex default, responsive font sizes,
 * and a class other code selects. Each of those is reproduced here, and
 * `text.test.tsx` pins the variant values against `theme(mode).typography` so the port
 * cannot drift from the theme it copies.
 *
 * Fidelity details worth knowing, all verified against the computed theme:
 *
 * - `caption` is `0.857rem` and `h3` is `3.43rem` — MUI rescales its defaults because
 *   `theme.typography.fontSize` is 16, not MUI's default 14. Transcribing the
 *   "obvious" 0.75rem/3rem would be wrong.
 * - `subtitle3`, `subtitle4` and `poster` are app-specific variants declared without a
 *   `fontFamily` or `lineHeight`, so they inherit both. They deliberately carry no
 *   `font-sans`/`leading-*` class here.
 * - `h1`, `subtitle3` and `poster` shrink below `lg` (`MuiTypography.styleOverrides`),
 *   which is expressible as `max-lg:` only because tailwind.css now mirrors MUI's
 *   breakpoints — `lg` is 1367px on both sides (see design/breakpoints.test.ts).
 * - In dark mode every Typography got `color="primary"` from `defaultProps`, and
 *   `primary.main` is pure white in both modes. That is `dark:text-primary` here.
 * - `.MuiTypography-root` is selected by 7 style modules and by
 *   `usePlanSearchNavigation`'s line-clamp measurement, so the class is kept on the
 *   element during the migration.
 */

/** MUI's default `variantMapping` — kept so migrated markup keeps its element. */
const ELEMENT_FOR: Record<string, ElementType> = {
    h1: 'h1',
    h2: 'h2',
    h3: 'h3',
    h4: 'h4',
    h5: 'h5',
    h6: 'h6',
    subtitle1: 'h6',
    subtitle2: 'h6',
    body1: 'p',
    body2: 'p',
    caption: 'span',
    subtitle3: 'span',
    subtitle4: 'span',
    poster: 'span',
};

export const textVariants = cva('outline-none no-underline', {
    variants: {
        variant: {
            body1: 'font-sans text-[1rem] font-normal leading-[1.5]',
            body2: 'font-sans text-[0.875rem] font-normal leading-[1.43]',
            subtitle1: 'font-sans text-[1.25rem] font-normal leading-[1.3]',
            subtitle2: 'font-sans text-[0.75rem] font-bold leading-[1.57] uppercase',
            subtitle3: 'text-[1.125rem] max-lg:text-[1rem]',
            subtitle4: 'text-[1.5rem] font-medium',
            caption: 'font-sans text-[0.8571428571428571rem] font-normal leading-[1.66]',
            h1: 'font-sans text-[2.62rem] font-light leading-[1.167] max-lg:text-[2.25rem]',
            h2: 'font-sans text-[2.62rem] font-light leading-[1.2]',
            h3: 'font-sans text-[3.4285714285714284rem] font-normal leading-[1.167]',
            h4: 'font-sans text-[2rem] font-semibold leading-[1.235]',
            h5: 'font-sans text-[2.5rem] font-bold leading-[1.334]',
            h6: 'font-sans text-[0.75rem] font-normal leading-[1.6]',
            poster: 'text-[3rem] leading-[1.125] max-lg:text-[2.25rem]',
        },
    },
    defaultVariants: { variant: 'body1' },
});

export type TextVariant = NonNullable<VariantProps<typeof textVariants>['variant']>;

/**
 * MUI's dark-mode default ink: `MuiTypography.defaultProps` sets `color="primary"` in
 * dark mode only, and an explicit `color` at the call site overrides it. Reproducing
 * that needs the default to be *absent* when a colour is given — baking
 * `dark:text-primary` into the base class would beat the caller's own `text-*` in dark
 * mode (twMerge keeps both: different variants, no conflict to resolve).
 */
const DARK_DEFAULT_INK = 'dark:text-primary';

/**
 * Readable text on the app's light surfaces (page `bg-default`, `bg-paper`, the
 * `styles.modal` overlay): the ink token in light mode, and the same white as
 * {@link DARK_DEFAULT_INK} in dark mode. Pass it as `color={TEXT_INK}`.
 */
export const TEXT_INK = 'text-text-primary dark:text-primary';

type TextProps = Omit<ComponentPropsWithoutRef<'p'>, 'color'> & {
    variant?: TextVariant;
    /** Override the element MUI's variantMapping would have chosen. */
    as?: ElementType;
    /**
     * Tailwind text-colour class, e.g. `text-text-secondary`. Passing any value
     * suppresses {@link DARK_DEFAULT_INK}, mirroring how an explicit MUI `color` prop
     * overrode `defaultProps`. Omit to inherit the mode default.
     *
     * Trap: `text-primary` is `--color-primary` (white in both modes), not the ink
     * token. For readable text pass {@link TEXT_INK}; for white on a dark fill, say
     * `text-white`. `design/textPrimaryInk.test.ts` keeps `text-primary` out.
     */
    color?: string;
    children?: ReactNode;
};

export const Text = ({ variant = 'body1', as, color, className, children, ...rest }: TextProps) => {
    const Component = as ?? ELEMENT_FOR[variant] ?? 'span';
    return (
        // `tabIndex={-1}` reproduces MuiTypography's defaultProps. It keeps the element
        // out of the tab order (where it already was) while staying programmatically
        // focusable — some editor focus logic depends on that.
        <Component
            tabIndex={-1}
            className={cn('MuiTypography-root', textVariants({ variant }), color ?? DARK_DEFAULT_INK, className)}
            {...rest}
        >
            {children}
        </Component>
    );
};
